import { apiRequest, apiFetch, authedRawFetch, ApiError } from '@/utils/apiUtils'
import { User, PersonalProfileFormData, BankInfoFormData, ThemeConfig, SystemSettings, IdCardSide, IdCardStatus, ProfileRequiredFieldsData, ProfileRequirementImpact } from '@/types'

/**
 * 密碼再驗證錯太多次時後端回 429；apiRequest 會把 429 的訊息加上「429: 」前綴，
 * 這裡拿掉前綴，讓確認框直接顯示「密碼錯誤次數過多，請 N 分鐘後再試」
 */
const withReadableVerificationError = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request()
  } catch (error) {
    if (error instanceof ApiError && error.status === 429) {
      throw new Error(error.message.replace(/^429:\s*/, ''))
    }
    throw error
  }
}

export const settingsService = {
  // 獲取用戶個人資料
  async getProfile(): Promise<User> {
    const response = await apiRequest<User>('/api/settings/profile', {
      method: 'GET'
    })
    return response.data
  },

  // 更新用戶個人資料
  async updateProfile(data: PersonalProfileFormData): Promise<User> {
    const response = await apiRequest<User>('/api/settings/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
    return response.data
  },

  // 上傳頭像
  async uploadAvatar(file: File): Promise<User> {
    const formData = new FormData()
    formData.append('avatar', file)

    const response = await fetch('/api/settings/avatar', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '頭像上傳失敗')
    }

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.message || '頭像上傳失敗')
    }

    return result.data
  },

  // 密碼驗證
  async verifyPassword(password: string): Promise<boolean> {
    const response = await apiRequest<{ success: boolean }>('/api/settings/verify-password', {
      method: 'POST',
      body: JSON.stringify({ password })
    })
    return response.success
  },

  // 查看完整身分證號碼（需密碼驗證）
  async getFullIdNumber(password: string): Promise<string> {
    const response = await withReadableVerificationError(() =>
      apiRequest<{ idNumber: string }>('/api/settings/id-number', {
        method: 'POST',
        body: JSON.stringify({ password })
      })
    )
    return response.data.idNumber
  },

  // ===== 身分證影本（正反面）=====

  // 查詢自己的上傳狀態（不含影像本體）
  async getIdCardStatus(): Promise<IdCardStatus> {
    const response = await apiRequest<IdCardStatus>('/api/settings/id-card', {
      method: 'GET'
    })
    return response.data
  },

  // 上傳（或覆蓋）單面身分證影本
  async uploadIdCard(side: IdCardSide, file: File): Promise<IdCardStatus> {
    const formData = new FormData()
    formData.append('image', file)

    const response = await authedRawFetch(`/api/settings/id-card/${side}`, {
      method: 'POST',
      body: formData
    })

    const result = await response.json().catch(() => ({ success: false, message: '上傳失敗' }))
    if (!response.ok || !result.success) {
      throw new Error(result.message || '身分證影本上傳失敗')
    }
    return result.data as IdCardStatus
  },

  // 刪除單面身分證影本
  async deleteIdCard(side: IdCardSide): Promise<IdCardStatus> {
    const response = await apiRequest<IdCardStatus>(`/api/settings/id-card/${side}`, {
      method: 'DELETE'
    })
    return response.data
  },

  /**
   * 取單面身分證影本的影像。
   * 端點需要 Authorization header，因此不能直接 <img src>，要拿 blob 自行轉 object URL。
   */
  async fetchIdCardImage(side: IdCardSide): Promise<Blob> {
    const response = await authedRawFetch(`/api/settings/id-card/${side}/image`, {
      method: 'GET'
    })
    if (!response.ok) {
      throw new Error(response.status === 404 ? '找不到影像' : '無法載入身分證影本')
    }
    return response.blob()
  },

  // 密碼由「恆遠會員中樞」統一管理（登入走 SSO proxy），
  // AnyTime 端無法修改，故移除 changePassword。

  // 更新銀行資料（後端要求重新輸入密碼確認身分，改完會通知本人與管理員）
  async updateBankInfo(data: BankInfoFormData, password: string): Promise<User> {
    const response = await withReadableVerificationError(() =>
      apiRequest<User>('/api/settings/bank-info', {
        method: 'PUT',
        body: JSON.stringify({ ...data, password })
      })
    )
    return response.data
  },

  // 獲取主題設定
  async getThemeConfig(): Promise<ThemeConfig> {
    const response = await apiRequest<ThemeConfig>('/api/settings/theme', {
      method: 'GET'
    })
    return response.data
  },

  // 更新主題設定
  async updateThemeConfig(data: ThemeConfig): Promise<ThemeConfig> {
    const response = await apiRequest<ThemeConfig>('/api/settings/theme', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
    return response.data
  },

  // 獲取系統設定
  async getSystemSettings(): Promise<SystemSettings> {
    const response = await apiRequest<SystemSettings>('/api/settings/system', {
      method: 'GET'
    })
    return response.data
  },

  // ===== 夥伴資料必填欄位設定（僅 ADMIN）=====

  // 取得目前必填欄位設定 + 欄位白名單 + 已存檔設定的影響人數
  async getProfileRequiredFields(): Promise<ProfileRequiredFieldsData> {
    const response = await apiRequest<ProfileRequiredFieldsData>('/api/settings/profile-required-fields', {
      method: 'GET'
    })
    return response.data
  },

  // 儲存必填欄位設定
  async updateProfileRequiredFields(requiredFields: string[]): Promise<ProfileRequiredFieldsData> {
    const response = await apiRequest<ProfileRequiredFieldsData>('/api/settings/profile-required-fields', {
      method: 'PUT',
      body: JSON.stringify({ requiredFields })
    })
    return response.data
  },

  // 預覽某候選設定（尚未存檔）會鎖住幾位夥伴
  async previewProfileRequiredFields(requiredFields: string[]): Promise<ProfileRequirementImpact> {
    const response = await apiRequest<ProfileRequirementImpact>('/api/settings/profile-required-fields/preview', {
      method: 'POST',
      body: JSON.stringify({ requiredFields })
    })
    return response.data
  },

  // 更新軟體名稱
  async updateSoftwareName(softwareName: string): Promise<{ softwareName: string }> {
    const response = await apiRequest<{ softwareName: string }>('/api/settings/system/software-name', {
      method: 'PUT',
      body: JSON.stringify({ softwareName })
    })
    return response.data
  },

  // 獲取軟體名稱（公開API，不需要身份驗證）
  async getPublicSoftwareName(): Promise<{ softwareName: string }> {
    try {
      const response = await apiFetch('/api/public/software-name', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error('無法獲取軟體名稱')
      }

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.message || '獲取軟體名稱失敗')
      }

      return result.data
    } catch (error) {
      console.warn('獲取軟體名稱失敗，使用預設值:', error)
      return { softwareName: 'AnyTime' }
    }
  },

  // Webhook 相關 API
  
  // 獲取 Webhook 設定
  async getWebhookSettings(): Promise<any> {
    const response = await apiRequest('/api/settings/webhooks', {
      method: 'GET'
    })
    return response.data
  },

  // 更新 Webhook 事件開關
  async updateWebhookEvents(events: Record<string, boolean>): Promise<void> {
    await apiRequest('/api/settings/webhooks/events', {
      method: 'PUT',
      body: JSON.stringify({ events })
    })
  },

  // 更新 PWA 通知事件開關
  async updatePWAEvents(events: Record<string, boolean>): Promise<void> {
    await apiRequest('/api/settings/webhooks/pwa-events', {
      method: 'PUT',
      body: JSON.stringify({ events })
    })
  },

  // 更新預設 Webhook URL
  async updateDefaultWebhookUrl(url: string): Promise<void> {
    await apiRequest('/api/settings/webhooks/default-url', {
      method: 'PUT',
      body: JSON.stringify({ url })
    })
  },

  // 測試 Webhook
  async testWebhook(eventType?: string): Promise<void> {
    await apiRequest('/api/settings/webhooks/test', {
      method: 'POST',
      body: JSON.stringify(eventType ? { eventType } : {})
    })
  },

  // 創建 Webhook 配置
  async createWebhookConfig(config: { eventType: string; url: string; isEnabled: boolean }): Promise<any> {
    const response = await apiRequest('/api/settings/webhooks/configs', {
      method: 'POST',
      body: JSON.stringify(config)
    })
    return response.data
  },

  // 更新 Webhook 配置
  async updateWebhookConfig(id: string, config: { eventType?: string; url?: string; isEnabled?: boolean }): Promise<any> {
    const response = await apiRequest(`/api/settings/webhooks/configs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(config)
    })
    return response.data
  },

  // 刪除 Webhook 配置
  async deleteWebhookConfig(id: string): Promise<void> {
    await apiRequest(`/api/settings/webhooks/configs/${id}`, {
      method: 'DELETE'
    })
  }
}
